import { faAngleDown, faFillDrip, faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, Dropdown, Slider } from 'antd';
import { clamp } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { images } from '../../../appCore';
import { boardSelector } from '../../../redux/board/reducer';
import { BOTTOM, BUS_EVENTS, CHART_MENU, COLOR_PALETTES, eventBus, getChartOptions, LEFT } from '../../../utils';
import Inspector from './components/Inspector';
import { styles } from './styles';

const LEGENDS = [
  { position: 'top', label: 'Top' },
  { position: 'left', label: 'Left' },
  { position: 'bottom', label: 'Bottom' },
  { position: 'right', label: 'Right' },
];

const POINTS = [
  { key: '10', title: 'Triangle' },
  { key: '9', title: 'Star' },
  { key: '8', title: 'RectRot' },
  { key: '7', title: 'Rounded' },
  { key: '6', title: 'Rect' },
  { key: '5', title: 'Line' },
  { key: '4', title: 'Dash' },
  { key: '3', title: 'CrossRot' },
  { key: '2', title: 'Cross' },
  { key: '1', title: 'Circle' },
];

const ALIGNS = [
  { key: '5', title: 'Right' },
  { key: '4', title: 'Bottom' },
  { key: '3', title: 'Left' },
  { key: '2', title: 'Top' },
  { key: '1', title: 'Center' },
];

const VALUES_COLORS = [
  { key: '2', title: 'Inherit' },
  { key: '1', title: 'Auto' },
];

let timer;

export default () => {
  const classes = styles();

  const { activeType } = useSelector(boardSelector);

  const initialState = {
    width: 800,
    height: 400,
    obj: {},
    type: 0,
    theme: [],
    padding: {
      isLock: false,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    legend: {
      display: true,
      position: 'top',
    },
    grid: {
      display: true,
      visible: false,
      color: '#666',
      lineWidth: 1,
      dash: {
        title: 'Solid',
        value: [],
      },
    },
    xAxis: {
      display: true,
      drawTicks: true,
      ticksDisplay: true,
      borderDisplay: true,
      visible: false,
      color: '#666',
      lineWidth: 1,
      position: BOTTOM,
      dash: {
        title: 'Solid',
        value: [],
      },
    },
    yAxis: {
      display: true,
      drawTicks: true,
      ticksDisplay: true,
      borderDisplay: true,
      visible: false,
      color: '#666',
      lineWidth: 1,
      position: LEFT,
      dash: {
        title: 'Solid',
        value: [],
      },
    },
    rAxis: {
      display: true,
      drawTicks: false,
      ticksDisplay: false,
      color: '#666',
      lineWidth: 1,
    },
    plot: {
      values: {
        display: false,
        align: 'center',
        color: 'Auto',
      },
      percentage: false,
      points: false,
      pointRadius: 3,
      pointBorderWidth: 1,
      pointStyle: 'Circle',
      tension: 0,
      lineStyle: {
        lineWidth: 3,
      },
      bar: {
        thickness: 80,
      },
      pie: {
        spacing: 0,
        borderRadius: 0,
        cutout: 0,
      },
    },
  };
  const [state, _setState] = useState(initialState);
  const setState = (obj) => _setState((_state) => ({ ..._state, ...obj }));

  const updateHTMLData = (data, time = 10) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const options = getChartOptions(data);
      eventBus.emit(BUS_EVENTS.UPDATE_HTML_COMPONENT, { id: state.obj.id, data: JSON.stringify(data), options });
      clearTimeout(timer);
    }, time);
  };

  const onUpdateState = ({ data, id }) => {
    const parsedData = JSON.parse(data);
    setState({
      ...parsedData,
      obj: {
        htmlContent: data,
        id,
      },
      type: parseInt(parsedData.type),
    });
  };

  const formatInputValue = (value, min = 0, max = 100) => {
    const v = parseInt(value);
    const clampedValue = clamp(v, min, max);
    const finalValue = isNaN(clampedValue) ? min : clampedValue;
    return finalValue;
  };

  const handleChangePadding = (e, pos) => {
    const value = formatInputValue(e.target.value, 0, 9999);
    let padding = {
      ...state.padding,
      [pos]: value,
    };
    if (state.padding.isLock) {
      padding.top = value;
      padding.left = value;
      padding.bottom = value;
      padding.right = value;
    }
    setState({ padding });
    const data = JSON.parse(obj.htmlContent);
    data.padding = padding;
    updateHTMLData(data, 10);
  };

  const handleChangePaddingLock = () => {
    let padding = {
      ...state.padding,
      isLock: !state.padding.isLock,
    };
    const data = JSON.parse(obj.htmlContent);
    if (padding.isLock) {
      const smallestValue = Math.min(padding.top, padding.left, padding.right, padding.bottom);
      padding.top = smallestValue;
      padding.left = smallestValue;
      padding.bottom = smallestValue;
      padding.right = smallestValue;
    }
    data.padding = padding;
    updateHTMLData(data);
  };

  const renderChartPadding = () => (
    <div className={classes.chartPadding}>
      <p className={classes.title}>Chart Padding</p>
      <div className={classes.chartPaddingContainer}>
        <div className={classes.chartPaddingOverlay}></div>
        <div className={classes.chartPaddingRow}>
          <div className={`${classes.border} ${classes.plotInput}`}>
            <input
              value={state.padding.top}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => handleChangePadding(e, 'top')}
            />
            <span>px</span>
          </div>
        </div>
        <div className={`${classes.chartPaddingRow} ${classes.spaceBetween}`}>
          <div className={`${classes.border} ${classes.plotInput}`}>
            <input
              value={state.padding.left}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => handleChangePadding(e, 'left')}
            />
            <span>px</span>
          </div>
          <div className={classes.chartPaddingIcon} onClick={handleChangePaddingLock}>
            <FontAwesomeIcon icon={state.padding.isLock ? faLock : faLockOpen} />
          </div>
          <div className={`${classes.border} ${classes.plotInput}`}>
            <input
              value={state.padding.right}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => handleChangePadding(e, 'right')}
            />
            <span>px</span>
          </div>
        </div>
        <div className={classes.chartPaddingRow}>
          <div className={`${classes.border} ${classes.plotInput}`}>
            <input
              value={state.padding.bottom}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => handleChangePadding(e, 'bottom')}
            />
            <span>px</span>
          </div>
        </div>
      </div>
    </div>
  );

  const handleChangeChartType = (i) => {
    const data = JSON.parse(state.obj.htmlContent);
    data.type = i;
    data.padding = {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
    updateHTMLData(data);
  };

  const renderChartType = () => (
    <div className={classes.chartType}>
      <p className={classes.title}>Chart Type</p>
      <ul>
        {CHART_MENU.map((c, i) => (
          <li key={i} className={state.type === i ? 'selected' : ''} onClick={() => handleChangeChartType(i)}>
            <img src={c.image} className={classes.chartImage} />
          </li>
        ))}
      </ul>
    </div>
  );

  const handleChangeChartTheme = (index) => {
    const data = JSON.parse(state.obj.htmlContent);
    if (JSON.stringify(COLOR_PALETTES[index]) === JSON.stringify(state.theme)) {
      data.theme = [];
      updateHTMLData(data);
      return;
    }
    data.series = data.series.map((s, i) => {
      const colorIndex = i % COLOR_PALETTES[index].length;
      return {
        ...s,
        color: COLOR_PALETTES[index][colorIndex],
      };
    });
    data.theme = COLOR_PALETTES[index];
    updateHTMLData(data);
  };

  const getChartThemeItems = () => {
    return COLOR_PALETTES.map((c, i) => ({
      key: i,
      label: (
        <div
          className={`${classes.palettes} ${
            JSON.stringify(COLOR_PALETTES[i]) === JSON.stringify(state.theme) ? 'selected' : ''
          }`}
          onClick={() => handleChangeChartTheme(i)}
        >
          {c.map((color, index) => (
            <div key={index} className={classes.palettesItem} style={{ backgroundColor: color }} />
          ))}
        </div>
      ),
    }));
  };

  const renderChartTheme = () => (
    <div className={classes.chartTheme}>
      <p className={classes.title}>Chart Theme</p>
      <Dropdown overlayClassName={classes.dropdown} menu={{ items: getChartThemeItems() }} trigger={'click'}>
        <div className={classes.themeContainer}>
          <div className={classes.themeInner}>
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className={classes.palettesHolder}
                style={{ backgroundColor: state.theme?.length > 0 ? state.theme[index] : '#fff' }}
              />
            ))}
          </div>
          <div className={classes.palettesIcon}>
            <FontAwesomeIcon icon={faAngleDown} />
          </div>
        </div>
      </Dropdown>
    </div>
  );

  const handleChangeChartLegendDisplay = (e) => {
    const legend = {
      ...state.legend,
      display: e.target.checked,
    };
    const data = JSON.parse(state.obj.htmlContent);
    data.legend = legend;
    updateHTMLData(data);
  };

  const handleChangeChartLegendPosition = (i) => {
    const legend = { ...state.legend, position: LEGENDS[i].position };

    const data = JSON.parse(state.obj.htmlContent);
    data.legend = legend;
    updateHTMLData(data);
  };

  const renderChartLegend = () => (
    <div className={classes.chartLegend}>
      <div className={`${classes.flexContainer} ${classes.legendContainer}`}>
        <Checkbox checked={state.legend.display} onChange={handleChangeChartLegendDisplay}>
          <p className={classes.title}>Legend</p>
        </Checkbox>
      </div>
      {state.legend.display && (
        <ul>
          {LEGENDS.map((legend, index) => (
            <li
              key={index}
              className={`${state.legend.position === legend.position ? 'selected' : ''}`}
              onClick={() => handleChangeChartLegendPosition(index)}
            >
              {legend.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const handleChangeChartGridDisplay = (e) => {
    const grid = {
      ...state.grid,
      display: e.target.checked,
    };
    const data = JSON.parse(state.obj.htmlContent);
    data.grid = grid;
    updateHTMLData(data);
  };

  const renderChartGrid = () => {
    if (['pie', 'polar', 'radar'].some((keyword) => CHART_MENU[state.type].title.toLowerCase().includes(keyword)))
      return;

    return (
      <div className={classes.chartGrid}>
        <div className={`${classes.flexContainer} ${classes.gridContainer}`}>
          <Checkbox checked={state.grid.display} onChange={handleChangeChartGridDisplay}>
            <p className={classes.title}>Grid</p>
          </Checkbox>
        </div>
        {state.grid.display && <Inspector obj={state.obj} initialState={state.grid} updateHTMLData={updateHTMLData} />}
      </div>
    );
  };

  const handleChangeChartAxisDisplay = (e, type) => {
    const data = JSON.parse(state.obj.htmlContent);
    data[type] = { ...data[type], display: e.target.checked };
    updateHTMLData(data);
  };

  const renderChartAxis = (type) => {
    if (CHART_MENU[state.type].title.toLowerCase().includes('pie')) return;

    const isRadarOrPolarChart = ['radar', 'polar'].some((keyword) =>
      CHART_MENU[state.type].title.toLowerCase().includes(keyword),
    );

    if ((type === 'xAxis' || type === 'yAxis') && isRadarOrPolarChart) return;

    if (type === 'rAxis' && !isRadarOrPolarChart) return;

    const stateData = state[type];

    return (
      <div className={classes.chartAxis}>
        <div className={`${classes.flexContainer} ${classes.axisContainer}`}>
          <Checkbox checked={stateData.display} onChange={(e) => handleChangeChartAxisDisplay(e, type)}>
            <p className={classes.title}>{type}</p>
          </Checkbox>
        </div>
        {stateData.display && (
          <Inspector
            obj={state.obj}
            initialState={stateData}
            updateHTMLData={updateHTMLData}
            axis={type}
            type={CHART_MENU[state.type].title.toLowerCase()}
          />
        )}
      </div>
    );
  };

  const handleChangeChartPlot = (e, type) => {
    let plot = {
      ...state.plot,
      [type]: type === 'tension' ? (e.target.checked ? 0.3 : 0) : e.target.checked,
      pointRadius: type === 'points' ? (e.target.checked ? 6 : 0) : state.plot.pointRadius,
    };
    if (type === 'values') {
      plot = {
        ...plot,
        values: {
          ...state.plot.values,
          display: e.target.checked,
          color: 'Auto',
        },
      };
    }
    const data = JSON.parse(state.obj.htmlContent);
    data.plot = plot;
    updateHTMLData(data);
  };

  const handleChangeValuesAlignAndColor = (i, type) => {
    let value = { align: ALIGNS[i].title };
    if (type === 'color') {
      value = { color: VALUES_COLORS[i].title };
    }
    const plot = {
      ...state.plot,
      values: {
        ...state.plot.values,
        ...value,
      },
    };
    const data = JSON.parse(state.obj.htmlContent);
    data.plot = plot;
    updateHTMLData(data);
  };

  const handleChangePointType = (i) => {
    const plot = {
      ...state.plot,
      pointBorderWidth: 1,
      pointRadius: 6,
      pointStyle: POINTS[i].title,
    };
    const data = JSON.parse(state.obj.htmlContent);
    data.plot = plot;
    updateHTMLData(data);
  };

  const handleChangeLineWidth = (e) => {
    const plot = {
      ...state.plot,
      lineStyle: { lineWidth: formatInputValue(e.target.value) },
    };
    setState({ plot });
    const data = JSON.parse(obj.htmlContent);
    data.plot = plot;
    updateHTMLData(data, 10);
  };

  const handleChangeBarThickness = (value) => {
    const plot = {
      ...state.plot,
      bar: { thickness: formatInputValue(value, 0, 100) },
    };
    setState({ plot });
    const data = JSON.parse(obj.htmlContent);
    data.plot = plot;
    updateHTMLData(data, 10);
  };

  const handleChangePieSettings = (value, type) => {
    const plot = {
      ...state.plot,
      pie: { ...state.plot.pie, [type]: formatInputValue(value) },
    };
    const data = JSON.parse(obj.htmlContent);
    data.plot = plot;
    updateHTMLData(data, 10);
  };

  const valuesColorItems = VALUES_COLORS.map((p, i) => ({
    key: p.key,
    label: <span onClick={() => handleChangeValuesAlignAndColor(i, 'color')}>{p.title}</span>,
  }));

  const alignItems = ALIGNS.map((p, i) => ({
    key: p.key,
    label: <span onClick={() => handleChangeValuesAlignAndColor(i)}>{p.title}</span>,
  }));

  const pointItems = POINTS.map((p, i) => ({
    key: p.key,
    label: <span onClick={() => handleChangePointType(i)}>{p.title}</span>,
  }));

  const renderChartPlot = () => {
    return (
      <div className={classes.chartPlot}>
        <p className={classes.title}>Plot</p>
        <div
          className={`${classes.flexContainer} ${classes.plotContainer}`}
          style={{ marginTop: 0, flexDirection: 'column', alignItems: 'start' }}
        >
          <Checkbox checked={state.plot.values.display} onChange={(e) => handleChangeChartPlot(e, 'values')}>
            <p className={classes.title}>Values</p>
          </Checkbox>
          {state.plot.values.display && (
            <div className={classes.flexContainer} style={{ marginTop: 0 }}>
              <div className={classes.plotValues}>
                <Dropdown
                  menu={{ items: valuesColorItems }}
                  trigger={['click']}
                  overlayClassName={classes.dropdownPlot}
                >
                  <div className={`${classes.border} ${classes.plotPointTypeContainer}`}>
                    <FontAwesomeIcon icon={faFillDrip} />
                    <span>{state.plot.values.color}</span>
                    <FontAwesomeIcon icon={faAngleDown} />
                  </div>
                </Dropdown>
              </div>
              <div className={classes.plotValues}>
                <Dropdown menu={{ items: alignItems }} trigger={['click']} overlayClassName={classes.dropdownPlot}>
                  <div className={`${classes.border} ${classes.plotPointTypeContainer}`}>
                    <img src={images.AWAY_CENTER} style={{ marginTop: 5 }} />
                    <span>{state.plot.values.align}</span>
                    <FontAwesomeIcon icon={faAngleDown} />
                  </div>
                </Dropdown>
              </div>
            </div>
          )}
        </div>
        {state.plot.values.display && (
          <div className={`${classes.flexContainer} ${classes.plotContainer}`}>
            <Checkbox checked={state.plot.percentage} onChange={(e) => handleChangeChartPlot(e, 'percentage')}>
              <p className={classes.title}>Render as percentages</p>
            </Checkbox>
          </div>
        )}
        {['area', 'line', 'radar'].some((keyword) => CHART_MENU[state.type].title.toLowerCase().includes(keyword)) &&
          !CHART_MENU[state.type].title.toLowerCase().includes('stacked area') && (
            <div className={`${classes.flexContainer} ${classes.plotContainer}`}>
              <Checkbox checked={state.plot.points} onChange={(e) => handleChangeChartPlot(e, 'points')}>
                <p className={classes.title}>Points</p>
              </Checkbox>
              {state.plot.points && (
                <div className={classes.plotPointType}>
                  <Dropdown menu={{ items: pointItems }} trigger={['click']} overlayClassName={classes.dropdownPlot}>
                    <div className={`${classes.border} ${classes.plotPointTypeContainer}`}>
                      <img src={images.POINT_STYLE} />
                      <span>{state.plot.pointStyle}</span>
                      <FontAwesomeIcon icon={faAngleDown} />
                    </div>
                  </Dropdown>
                </div>
              )}
            </div>
          )}
        {['area', 'line', 'radar'].some((keyword) => CHART_MENU[state.type].title.toLowerCase().includes(keyword)) && (
          <div className={`${classes.flexContainer} ${classes.plotContainer}`}>
            <Checkbox checked={state.plot.tension} onChange={(e) => handleChangeChartPlot(e, 'tension')}>
              <p className={classes.title}>Smooth</p>
            </Checkbox>
          </div>
        )}
        {['line', 'radar'].some((keyword) => CHART_MENU[state.type].title.toLowerCase().includes(keyword)) && (
          <div className={`${classes.flexContainer} ${classes.plotContainer}`}>
            <p className={classes.title}>Line Style</p>
            <div className={`${classes.border} ${classes.plotInput}`}>
              <input
                value={state.plot.lineStyle.lineWidth}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                onChange={handleChangeLineWidth}
              />
              <span>px</span>
            </div>
          </div>
        )}
        {['bar', 'column'].some((keyword) => CHART_MENU[state.type].title.toLowerCase().includes(keyword)) && (
          <>
            <p className={classes.title}>Thickness</p>
            <div className={`${classes.flexContainer} ${classes.plotContainer}`} style={{ margin: 0 }}>
              <div className={`${classes.border} ${classes.plotInput}`}>
                <input
                  value={state.plot.bar.thickness}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  onChange={(e) => handleChangeBarThickness(e.target.value)}
                />
                <span>%</span>
              </div>
              <Slider
                className={classes.slider}
                value={state.plot.bar.thickness}
                min={0}
                max={100}
                step={1}
                onChange={handleChangeBarThickness}
              />
            </div>
          </>
        )}
        {['pie'].some((keyword) => CHART_MENU[state.type].title.toLowerCase().includes(keyword)) && (
          <>
            {[
              { label: 'Padding', key: 'spacing' },
              { label: 'Corner Radius', key: 'borderRadius' },
              { label: 'Inner Radius', key: 'cutout' },
            ].map((item) => (
              <div key={item.key}>
                <p className={classes.title}>{item.label}</p>
                <div className={`${classes.flexContainer} ${classes.plotContainer}`} style={{ margin: 0 }}>
                  <div className={`${classes.border} ${classes.plotInput}`}>
                    <input
                      value={state.plot.pie[item.key]}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      onChange={(e) => handleChangePieSettings(e.target.value, item.key)}
                    />
                    <span>%</span>
                  </div>
                  <Slider
                    className={classes.slider}
                    value={state.plot.pie[item.key]}
                    min={0}
                    max={100}
                    step={1}
                    onChange={(e) => handleChangePieSettings(e, item.key)}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  const obj = window.canvas.getActiveObject();
  useEffect(() => {
    const data = JSON.parse(obj.htmlContent);
    setState({
      ...data,
      obj,
      type: parseInt(data.type),
    });
  }, [obj]);

  useEffect(() => {
    eventBus.on(BUS_EVENTS.UPDATE_CHART_OBJECT, onUpdateState);
    return () => {
      eventBus.off(BUS_EVENTS.UPDATE_CHART_OBJECT, onUpdateState);
    };
  });

  return activeType ? (
    <div className="mo__chart-settings">
      {renderChartPadding()}
      {renderChartType()}
      {renderChartTheme()}
      {renderChartLegend()}
      {renderChartGrid()}
      {renderChartAxis('xAxis')}
      {renderChartAxis('yAxis')}
      {renderChartAxis('rAxis')}
      {renderChartPlot()}
    </div>
  ) : (
    <div />
  );
};
